import type { MissingInput, PlanItem, RefinementResult } from "../../types";

/**
 * W4-03 — the pure copy-generation functions shared by the visual change
 * summary (`RefinementChangeSummary.tsx`) and the live-region announcement
 * in `useRefinementFlow.ts`. Kept in one place so the two can never say two
 * different things about the same answer. See
 * docs/design/features/W4-03.md "Change summary".
 *
 * Deliberately its own module, not part of `RefinementChangeSummary.tsx`:
 * a file that exports both a component and plain functions defeats Fast
 * Refresh's ability to preserve component state across an edit
 * (react-refresh/only-export-components).
 */

const AGGREGATE_SENTENCES: { code: string; sentence: string }[] = [
  { code: "monthly_reserve", sentence: "Your monthly reserve changed." },
  { code: "outgoing_12_month", sentence: "Your 12-month outgoing total changed." },
  { code: "potential_benefits", sentence: "Your potential benefits changed." },
];

function changedItemsSentence(codes: string[], items: PlanItem[]): string | null {
  if (codes.length === 0) return null;
  const byCode = new Map(items.map((item) => [item.code, item.english_name]));
  // A code the payload doesn't resolve is still counted, never silently
  // dropped — see docs/architecture/features/W4-03.md §4 on `.get()` over
  // `[]` indexing for historical/unfamiliar codes.
  const names = codes.map((code) => byCode.get(code)).filter((name): name is string => Boolean(name));
  if (codes.length === 1) {
    return names.length === 1 ? `One estimate changed: ${names[0]}.` : "One estimate changed.";
  }
  return names.length > 0
    ? `${codes.length} estimates changed: ${names.join(", ")}.`
    : `${codes.length} estimates changed.`;
}

function changedAggregateSentences(codes: string[]): string[] {
  const named = new Set(codes);
  return AGGREGATE_SENTENCES.filter((row) => named.has(row.code)).map((row) => row.sentence);
}

/**
 * Names only the invalidated inputs that are actually going to be re-asked
 * in this pass — `queue` is the *new* snapshot's validated queue. An input
 * that was invalidated but does not reappear as a blocker will not be
 * re-asked, and this deliberately says nothing about it rather than
 * promising a question that will never come.
 */
function invalidatedSentence(invalidatedCodes: string[], queue: MissingInput[]): string | null {
  if (invalidatedCodes.length === 0) return null;
  const stillAsking = queue.filter((blocker) => invalidatedCodes.includes(blocker.code));
  if (stillAsking.length === 0) return null;
  if (stillAsking.length === 1) {
    return `We'll need to ask about ${stillAsking[0].label} again, because this answer changes it.`;
  }
  return `We'll need to ask ${stillAsking.length} earlier answers again, because this answer changes them.`;
}

export interface RefinementChangeText {
  heading: string;
  lines: string[];
}

/**
 * The single source of truth for what an answer's consequence reads as.
 * `result === null` means "recalculated, and we won't characterise the
 * change" — never "nothing changed". `material_change: false` is its own
 * distinct, honest outcome. See docs/design/features/W4-03.md "Copy/content".
 */
export function describeRefinementChange(
  result: RefinementResult | null,
  items: PlanItem[],
  queue: MissingInput[],
): RefinementChangeText {
  if (result === null) {
    return { heading: "Saved. Your estimates were recalculated.", lines: [] };
  }
  if (!result.material_change) {
    return {
      heading: "Your estimates were recalculated. Nothing changed for your situation.",
      lines: [],
    };
  }
  const lines: string[] = [];
  const itemsLine = changedItemsSentence(result.changed_item_codes, items);
  if (itemsLine) lines.push(itemsLine);
  lines.push(...changedAggregateSentences(result.changed_aggregate_codes));
  const invalidated = invalidatedSentence(result.invalidated_input_codes, queue);
  if (invalidated) lines.push(invalidated);
  return { heading: "Your estimates were updated", lines };
}

/** One flat sentence for the polite live region — never repeats the value the user typed. */
export function refinementAnnouncement(
  result: RefinementResult | null,
  items: PlanItem[],
  queue: MissingInput[],
): string {
  const { heading, lines } = describeRefinementChange(result, items, queue);
  return [heading, ...lines].join(" ");
}
